import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Wallet, BarChart3, ShoppingCart, Package, Users, Briefcase, ScrollText, LogOut,
  ChevronDown, Shield,
  type LucideIcon,
} from 'lucide-react'
import { api } from '../lib/api'
import { clearTokens } from '../lib/auth'
import { useEnabledApps } from '../lib/useEnabledApps'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

// Permission → module info. `appCode` ties each entry to a row in the App
// catalog so the card disappears when an admin disables the module.
// `isEmployeeApp` lifts the entry into the Employee Apps section (rendered
// only for users holding the Employee role, i.e. with `employee.portal_access`).
type ModuleDef = {
  permission: string
  appCode: string
  code: string
  name: string
  description: string
  Icon: LucideIcon
  url: string
  isEmployeeApp?: boolean
}

const MODULE_DEFINITIONS: ModuleDef[] = [
  {
    permission: 'finance.access', appCode: 'finance', code: 'finance', name: 'Finance', Icon: Wallet,
    description: 'Invoices, payments, bills, journal entries.',
    url: '/admin/finance/invoices',
  },
  {
    permission: 'finance.reports', appCode: 'finance', code: 'finance.reports', name: 'Financial Reports', Icon: BarChart3,
    description: 'Trial Balance, P&L, Balance Sheet.',
    url: '/admin/finance/reports/profit-loss',
  },
  {
    permission: 'sales.access', appCode: 'sales', code: 'sales', name: 'Sales', Icon: ShoppingCart,
    description: 'Quotes and sales orders.',
    url: '/admin/sales/quotes',
  },
  {
    permission: 'inventory.access', appCode: 'inventory', code: 'inventory', name: 'Inventory', Icon: Package,
    description: 'Products, warehouses, stock movements.',
    url: '/admin/inventory/products',
  },
  {
    permission: 'hr.access', appCode: 'hr', code: 'hr', name: 'Human Resources', Icon: Users,
    description: 'People, employments, departments, time off.',
    url: '/admin/hr/people',
  },
  {
    permission: 'crm.access', appCode: 'crm', code: 'crm', name: 'CRM', Icon: Briefcase,
    description: 'Leads, accounts, contacts, opportunities.',
    url: '/admin/crm/dashboard',
  },
  {
    permission: 'consulting.submit_time', appCode: 'consulting', code: 'work-report',
    name: 'Work Report', Icon: ScrollText,
    description: 'Log hours worked on your assigned projects.',
    url: '/my-apps/work-report',
  },
]

export default function UserPortal() {
  const navigate = useNavigate()

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/auth/me/')).data,
  })

  const handleLogout = () => {
    clearTokens()
    navigate('/login', { replace: true })
  }

  const user = meQ.data
  const permissions: string[] = user?.permissions || []
  const isAdmin = !!(user?.is_staff || user?.is_superuser)
  const displayName: string = user?.person_name || user?.first_name || user?.username || 'User'
  const initial = (displayName || 'U').charAt(0).toUpperCase()
  const personId: number | null = user?.person_id || null
  const { isEnabled: isAppEnabled } = useEnabledApps()
  // A user sees a card only when they have permission AND the module is
  // currently enabled in the App catalog.
  const accessibleModules = MODULE_DEFINITIONS.filter(
    (m) => permissions.includes(m.permission) && isAppEnabled(m.appCode)
  )
  const employeeModules = accessibleModules.filter((m) => m.isEmployeeApp)
  const otherModules = accessibleModules.filter((m) => !m.isEmployeeApp)
  // Employee section is rendered only for users in the Employee role.
  const isEmployee = permissions.includes('portal.portal_access')

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold">MooderERP</h1>

          {/* User menu — top-right anchor. Mirrors the admin layout pattern
              so the portal feels like the same product. Admins get an extra
              "Switch to admin" link so they can jump between the two surfaces
              without re-typing the URL. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted transition"
                aria-label="Open user menu"
              >
                <span className="h-7 w-7 rounded-full bg-primary/15 text-primary text-xs font-semibold inline-flex items-center justify-center">
                  {initial}
                </span>
                <span className="hidden sm:inline max-w-[14ch] truncate">{displayName}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-medium truncate">{displayName}</div>
                {user?.email && (
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {personId && (
                <DropdownMenuItem onClick={() => navigate(`/admin/hr/people/${personId}`)}>
                  My profile
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate('/admin')}>
                  <Shield className="h-3.5 w-3.5 mr-2" /> Switch to admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-bold mb-1">
          Welcome{user?.first_name ? `, ${user.first_name}` : ''}
        </h2>
        <p className="text-sm text-muted-foreground mb-8">
          Signed in as <span className="font-mono">{user?.username || '…'}</span>
        </p>

        {/* Employee Apps — only rendered for active employees (Employee role). */}
        {isEmployee && (
          <section className="mb-8">
            <h3 className="font-semibold text-foreground mb-1">Employee Apps</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Self-service tools for active employees — leave requests, timesheets, internal knowledge base, surveys.
            </p>
            {employeeModules.length === 0 ? (
              <div className="bg-card rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                Employee apps will appear here as they roll out. Leave Request and Timesheet are next.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {employeeModules.map((m) => <ModuleCard key={m.code} m={m} />)}
              </div>
            )}
          </section>
        )}

        {/* Modules the user can access */}
        <section className="mb-8">
          <h3 className="font-semibold text-foreground mb-3">
            {isEmployee ? 'Other modules' : 'Your modules'}
          </h3>
          {meQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : otherModules.length === 0 && !isEmployee ? (
            <div className="bg-card rounded-lg border border-border p-6 text-sm text-muted-foreground">
              You don't have permission to access any modules yet. Ask an administrator to assign you a role.
            </div>
          ) : otherModules.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-6 text-sm text-muted-foreground">
              No additional modules. Employee Apps above are everything you have access to.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {otherModules.map((m) => <ModuleCard key={m.code} m={m} />)}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile card */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-semibold mb-3">Your account</h3>
            {meQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <dl className="space-y-2 text-sm">
                <Row k="Email" v={user?.email || '—'} />
                <Row
                  k="Name"
                  v={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || '—'}
                />
                <Row
                  k="Roles"
                  v={user?.roles?.length ? user.roles.map((r: any) => r.name).join(', ') : '—'}
                />
                <Row
                  k="Last login"
                  v={user?.last_login ? new Date(user.last_login).toLocaleString() : 'First time'}
                />
              </dl>
            )}
          </div>

          {/* Permissions list */}
          <div className="bg-card border border-border rounded-lg p-6 lg:col-span-2">
            <h3 className="font-semibold mb-3">Your permissions</h3>
            {permissions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No permissions assigned.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {permissions.map((p) => (
                  <span
                    key={p}
                    className="text-xs bg-muted text-muted-foreground border border-border px-2 py-1 rounded font-mono"
                  >
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

function Row({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex justify-between">
      <dt className="font-medium text-muted-foreground">{k}</dt>
      <dd className="text-foreground text-right">{v}</dd>
    </div>
  )
}

function ModuleCard({ m }: { m: ModuleDef }) {
  const Icon = m.Icon
  return (
    <Link
      to={m.url}
      className="block bg-card border border-border rounded-lg p-4 hover:border-primary hover:shadow-sm transition group"
    >
      <div className="flex items-start space-x-3">
        <div className="rounded-md bg-primary/10 text-primary p-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground">{m.name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{m.description}</div>
        </div>
      </div>
    </Link>
  )
}
